import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabase";
import { useAuth } from "../system/AppGate";
import { useLabPermissions } from "../../hooks/useLabPermissions";

// Modal: ABM categorías de instrumentos (Ingresos)
// Ubicación esperada: src/pages/ingresos/components/InstrumentCategoriesModal.jsx
import InstrumentCategoriesModal from "../../pages/ingresos/components/InstrumentCategoriesModal";

// util clases
const cx = (...classes) => classes.filter(Boolean).join(" ");

const PrimaryTab = ({ to, label, active, color }) => {
  const colorMap = {
    sky: "text-sky-300 hover:text-sky-200",
    indigo: "text-indigo-300 hover:text-indigo-200",
    emerald: "text-emerald-300 hover:text-emerald-200",
    amber: "text-amber-300 hover:text-amber-200",
  };

  return (
    <NavLink
      to={to}
      className={cx(
        "px-4 py-2 rounded-xl font-semibold transition-colors",
        colorMap[color],
        active ? "nav-active-item active-indicator" : "hover:bg-white/5"
      )}
    >
      {label}
    </NavLink>
  );
};

const SubTab = ({ to, label, exact = false, color }) => {
  const colorBg = {
    sky: "hover:bg-sky-400/15",
    indigo: "hover:bg-indigo-400/15",
    emerald: "hover:bg-emerald-400/15",
    amber: "hover:bg-amber-400/15",
  };

  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        cx(
          "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
          colorBg[color],
          isActive ? "nav-active-item active-indicator" : "text-muted-foreground"
        )
      }
    >
      {label}
    </NavLink>
  );
};

export default function Header({
  appTheme = "violet",
  appThemeOptions = [],
  onAppThemeChange = () => {},
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Modal: Categorías de instrumentos (solo Ingresos)
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const { user, profile, role, loading } = useAuth();
  const { isAdmin } = useLabPermissions();

  // ⛔ nunca renderizar sin rol resuelto
  if (loading || !role) return null;

  const isTechnician = role === "technician";
  const isLabManager = role === "lab_manager";
  const isCommercial = role === "commercial";
  const isAccountant = role === "accountant";
  const isAdministrativeAssistant = role === "administrative_assistant";

  // Home según rol (click en logo)
  const goHome = () => {
    if (isTechnician) return navigate("/ops/my-pos");
    if (isAdministrativeAssistant) return navigate("/ingresos");
    if (isLabManager) return navigate("/ops/my-pos");
    if (isAccountant) return navigate("/contabilidad/facturacion");
    if (isCommercial) return navigate("/offers");
    return navigate("/offers"); // admin
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  // Sección activa
  const section = React.useMemo(() => {
    const p = location.pathname;

    if (p.startsWith("/ingresos")) return "ingresos";
    if (p.startsWith("/facturacion/laboratorio")) return "billingLab";
    if (p.startsWith("/contabilidad")) return "accounting";
    if (p.startsWith("/ops") || p.startsWith("/instrument-details")) return "operations";
    if (
      p.startsWith("/quality-compliance") ||
      p.startsWith("/quality/reference-standards")
    )
      return "quality";
    if (p.startsWith("/admin")) return "admin";
    if (p.startsWith("/offers") || p.startsWith("/client-analytics")) return "commercial";

    return "commercial";
  }, [location.pathname]);

  const colorBySection = {
    commercial: "sky",
    ingresos: "sky",
    billingLab: "amber",
    operations: "indigo",
    accounting: "amber",
    quality: "emerald",
    admin: "amber",
  };

  const color = colorBySection[section];

  /**
   * ============================
   * TABS PRINCIPALES
   * ============================
   */
  const primaryTabs = [
    // 🧾 INGRESOS → admin y lab_manager
    (isAdmin || isLabManager || isAdministrativeAssistant) && {
      key: "ingresos",
      to: "/ingresos",
      label: "Ingresos",
      color: "sky",
      active: section === "ingresos",
    },

    // 🔵 COMERCIAL → SOLO admin o commercial
    (isAdmin || isCommercial) && {
      key: "commercial",
      to: "/offers",
      label: "Comercial",
      color: "sky",
      active: section === "commercial",
    },

    // Facturación gerencial -> lab_manager y admin
    isAdmin && {
      key: "billingLab",
      to: "/facturacion/laboratorio",
      label: "Facturación",
      color: "amber",
      active: section === "billingLab",
    },

    // Contabilidad -> solo accountant
    isAccountant && {
      key: "accounting",
      to: "/contabilidad/facturacion",
      label: "Contabilidad",
      color: "amber",
      active: section === "accounting",
    },

    // 🟣 OPERACIONES → todos menos commercial puro
    !isCommercial && !isAccountant && !isAdministrativeAssistant && {
      key: "operations",
      to: "/ops/my-pos",
      label: "Operaciones",
      color: "indigo",
      active: section === "operations",
    },

    // 🟢 CALIDAD → admin y lab_manager
    (isAdmin || isLabManager) && {
      key: "quality",
      to: "/quality-compliance",
      label: "Calidad",
      color: "emerald",
      active: section === "quality",
    },

    // 🟡 ADMIN → solo admin
    isAdmin && {
      key: "admin",
      to: "/admin/technicians",
      label: "Admin",
      color: "amber",
      active: section === "admin",
    },
  ].filter(Boolean);

  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur border-b border-border">
      {/* ============================
          BARRA SUPERIOR
      ============================ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <button onClick={goHome} className="font-bold app-title-gradient">
            Calins App
          </button>

          <nav className="hidden md:flex items-center gap-1">
              {primaryTabs.map(({ key, ...tab }) => (
                <PrimaryTab key={key} {...tab} />
              ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <label className="sr-only" htmlFor="calins-theme-selector">
              Seleccionar tema de color
            </label>
            <select
              id="calins-theme-selector"
              className="btn-ghost h-9"
              value={appTheme}
              onChange={(event) => onAppThemeChange(event.target.value)}
              aria-label="Seleccionar tema de color"
            >
              {appThemeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="text-sm text-muted-foreground hidden sm:block">
            {profile?.full_name || user?.email}
          </div>

          <button onClick={handleLogout} className="btn-ghost">
            Salir
          </button>
        </div>
      </div>

      {/* ============================
          SUBMENÚ
      ============================ */}
      <div className="border-t border-border app-sidebar-bg">
        <div className="max-w-7xl min-w-0 mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center gap-2 overflow-x-auto overscroll-x-contain">
          {/* 🔵 SUBMENÚ INGRESOS */}
          {section === "ingresos" &&
            (isAdmin || isLabManager || isAdministrativeAssistant) && (
            <>
              <SubTab to="/ingresos" label="Ingresos" color="sky" exact />

              {/* Acciones rápidas de Ingresos */}
              {(isAdmin || isLabManager) && (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCategoriesOpen(true)}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/25"
                    title="Agregar / editar / eliminar categorías de instrumentos"
                  >
                    + Categorías
                  </button>
                </div>
              )}
            </>
          )}

          {/* 🔵 SUBMENÚ COMERCIAL */}
          {section === "commercial" && (isAdmin || isCommercial) && (
            <>
              <SubTab to="/offers" label="Ofertas" color="sky" exact />
              <SubTab to="/offers-stats" label="Estadísticas" color="sky" />
              <SubTab to="/client-analytics" label="Clientes" color="sky" />
              <SubTab to="/client-360" label="Reportes" color="sky" />
            </>
          )}

          {/* 🟣 SUBMENÚ OPERACIONES */}
          {section === "operations" && (
            <>
              <SubTab
                to="/ops/my-pos"
                label={(isAdmin || isLabManager) ? "OS del laboratorio" : "Mis OS"}
                color="indigo"
              />
              {(isAdmin || isLabManager) && (
                <SubTab to="/ops/service-orders" label="Instrumentos de OS" color="indigo" />
              )}
              {(isAdmin || isLabManager) && (
                <SubTab to="/ops/certificate-approvals" label="Certificados a aprobar" color="indigo" />
              )}
              {(isAdmin || isLabManager) && (
                <SubTab to="/ops/certificates" label="Buscar certificados" color="indigo" />
              )}
              {(isAdmin || isLabManager) && (
                <SubTab to="/ops/certificate-archive" label="Archivo de certificados" color="indigo" />
              )}
              <SubTab to="/ops/manual" label="Manual" color="indigo" />
              {isTechnician && (
                <div className="ml-auto text-xs italic text-muted-foreground">Panel de Técnico</div>
              )}
            </>
          )}

          {section === "billingLab" && isAdmin && (
            <>
              <SubTab to="/facturacion/laboratorio" label="Bandeja" color="amber" exact />
            </>
          )}

          {section === "quality" && (isAdmin || isLabManager) && (
            <>
              <SubTab
                to="/quality-compliance"
                label="Cumplimiento"
                color="emerald"
                exact
              />
              <SubTab
                to="/quality/reference-standards"
                label="Instrumentos Patrones"
                color="emerald"
              />
            </>
          )}

          {section === "admin" && isAdmin && (
            <>
              <SubTab to="/admin/users" label="Usuarios" color="amber" exact />
              <SubTab to="/admin/technicians" label="Técnicos" color="amber" exact />
              <SubTab to="/admin/exports" label="Exportaciones" color="amber" />
              <SubTab to="/admin/instrument-duplicates" label="Duplicados" color="amber" />
              <SubTab to="/admin/portal-users" label="Portal clientes" color="amber" />
            </>
          )}

          {section === "accounting" && isAccountant && (
            <>
              <SubTab to="/contabilidad/facturacion" label="Bandeja" color="amber" exact />
            </>
          )}
        </div>
      </div>

      {/* Modal categorías (se monta en el Header para que el botón esté en la barra) */}
      <InstrumentCategoriesModal open={categoriesOpen} onClose={() => setCategoriesOpen(false)} />
    </header>
  );
}
